import * as React from "react";
import { useSearchParams } from "react-router";
import { Blocks, Eye, History } from "lucide-react";
import {
  MAX_PROMPT_CHARS,
  createPromptVersionSchema,
  moduleOrder,
  type AdminModule,
  type PromptVersion,
} from "@taskiq/shared";
import { ModuleConfigForm } from "@/components/modules/ModuleConfigForm";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useConfirm } from "@/components/ui/confirm-context";
import { EmptyState, ErrorState, FormMessage, Spinner } from "@/components/ui/Feedback";
import { Field, Form, Input } from "@/components/ui/Field";
import { IconButton } from "@/components/ui/IconButton";
import { InfoHint } from "@/components/ui/InfoHint";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Segmented } from "@/components/ui/Segmented";
import { Switch } from "@/components/ui/Switch";
import { Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { useToast } from "@/components/ui/toast-context";
import { browserTimezone } from "@/components/ui/TimezoneSelect";
import { ModuleIcon, moduleGlyph } from "@/components/visit/ModuleIcon";
import { api, apiErrorMessage, apiFieldErrors } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { fieldErrorsFrom } from "@/lib/auth-errors";
import { cn } from "@/lib/cn";
import { formatDayTimeIn } from "@/lib/dates";
import { useApi } from "@/lib/useApi";

/**
 * The catalogue's two editable halves, per module: the prompt the model is asked
 * with, and the configuration the check runs with. Superadmin only - the nav
 * entry is gated on `platform.admin`, and the API refuses everyone else.
 *
 * ── Two things, kept apart ──────────────────────────────────────────────────
 *
 * The prompt is **how to ask**; the configuration is **what to ask** - the
 * checklist, the capture window, the frame budget. The checklist never appears
 * in a prompt: its codes are templated into the request at call time, so adding
 * an item is a configuration edit the model picks up with no prompt change.
 * That is why they are two sub-views rather than one form.
 *
 * ── Global and immediate, said where it is decided ──────────────────────────
 *
 * There is one row per module for every customer. Activating a prompt or saving
 * a setting changes the next check anybody runs, anywhere, and there is no
 * staging copy. That is said in the activation's confirmation and in the toast
 * after a save, rather than in a banner that sat on every view and was read
 * once. What a field is for is behind the mark beside its label, for the same
 * reason: this page is a form, and a line of help under every control made it
 * a document.
 *
 * ── A mini nav, like the account page ───────────────────────────────────────
 *
 * The module and the sub-view are in the search params rather than in state, so
 * "look at the PPE checklist" is a link. They are plain buttons announced as
 * the current page, for the reason `Profile` gives: panels rendered one at a
 * time and reflected in the URL are a small router, not a tab widget.
 */

type View = "prompt" | "config";

const VIEWS: readonly { value: View; label: string }[] = [
  { value: "prompt", label: "Prompt" },
  { value: "config", label: "Configuration" },
];

interface ModuleList {
  modules: AdminModule[];
}

export function Modules() {
  const { data, error, isLoading, mutate } = useApi<ModuleList>("/modules");
  const [params, setParams] = useSearchParams();
  const confirm = useConfirm();

  // Reported up by whichever panel is open, so leaving it can ask first. A
  // prompt is up to twenty thousand characters somebody typed, and a tab press
  // is not a request to throw them away.
  const [dirty, setDirty] = React.useState(false);

  const modules = React.useMemo(
    () => [...(data?.modules ?? [])].sort((a, b) => moduleOrder(a.code) - moduleOrder(b.code)),
    [data]
  );

  const current = modules.find((module) => module.code === params.get("module")) ?? modules[0];
  // A module no model reads has no prompt to show, whatever the URL asked for.
  const view: View =
    current && !current.prompted ? "config" : params.get("view") === "config" ? "config" : "prompt";

  async function go(next: { module: string; view: View }) {
    if (next.module === current?.code && next.view === view) return;
    if (dirty) {
      const discard = await confirm({
        title: "Discard changes?",
        message: "Your unsaved changes will be lost.",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        danger: true,
      });
      if (!discard) return;
    }
    setDirty(false);
    setParams(next.view === "config" ? next : { module: next.module });
  }

  // The response carries the module as it now stands, so the list takes it
  // rather than asking again. `updated_at` moves with it, which is what re-seeds
  // the configuration form below.
  const replaceModule = React.useCallback(
    (next: AdminModule) => {
      void mutate(
        (prev) =>
          prev && {
            modules: prev.modules.map((module) => (module.code === next.code ? next : module)),
          },
        { revalidate: false }
      );
    },
    [mutate]
  );

  return (
    <div className="page">
      <PageHeader title="Modules" />

      {error && <ErrorState error={error} onRetry={() => void mutate()} />}

      {isLoading && (
        <div className="modules-loading">
          <Spinner className="spinner--lg" />
          <span className="sr-only">Loading modules</span>
        </div>
      )}

      {data && modules.length === 0 && (
        <Card>
          <EmptyState
            icon={<Blocks />}
            title="No modules in the catalogue"
            description="The catalogue is seeded by the bootstrap script. Until it runs there is nothing to configure."
          />
        </Card>
      )}

      {current && (
        <div className="subnav-layout">
          <nav className="subnav" aria-label="Modules">
            {modules.map((module) => {
              const Glyph = moduleGlyph(module.code);
              const active = module.code === current.code;
              return (
                <button
                  key={module.code}
                  type="button"
                  aria-current={active ? "page" : undefined}
                  onClick={() => void go({ module: module.code, view })}
                  className={cn("subnav__link", active && "subnav__link--active")}
                >
                  <Glyph aria-hidden />
                  {module.label}
                </button>
              );
            })}
          </nav>

          <div className="subnav-layout__panel">
            <div className="stack">
              <div className="module-head">
                <div className="module-head__identity">
                  <ModuleIcon code={current.code} />
                  <div className="module-head__text">
                    <h2 className="module-head__title">
                      {current.label}
                      {/* Only the exception is badged. "Active" on every module
                          but one says nothing the rail does not. */}
                      {!current.is_active && <Badge tone="neutral">Switched off</Badge>}
                    </h2>
                  </div>
                </div>

                {current.prompted && (
                  <Segmented
                    label="What to edit"
                    value={view}
                    options={VIEWS}
                    onChange={(next) => void go({ module: current.code, view: next })}
                  />
                )}
              </div>

              {view === "prompt" ? (
                <PromptPanel
                  key={current.code}
                  module={current}
                  onDirtyChange={setDirty}
                  onModuleChange={replaceModule}
                />
              ) : (
                <ModuleConfigForm
                  // Re-seeded from the stored row whenever it changes, which
                  // after a save is the value the API just wrote.
                  key={`${current.code}:${current.updated_at}`}
                  module={current}
                  onDirtyChange={setDirty}
                  onSaved={replaceModule}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── the prompt ─────────────────────────────── */

interface VersionList {
  versions: PromptVersion[];
}

interface VersionResult {
  version: PromptVersion;
  module: AdminModule;
}

/**
 * The active prompt in an editor, and every version before it.
 *
 * ── Saving never edits ──────────────────────────────────────────────────────
 *
 * A version is what produced every answer that points at it, so its text is
 * never changed. "Save as new version" writes the next one, and rolling back is
 * activating an older one from the history. The editor therefore has no notion
 * of which version it is "editing": it starts from the active text, and what it
 * saves is new.
 *
 * ── The draft is only a draft once it differs ───────────────────────────────
 *
 * `draft` is null until somebody types, and the editor shows the active body
 * underneath it. So activating another version while nothing is typed moves the
 * editor with it, and a save or a discard is just clearing the draft - there is
 * no effect re-seeding state from props and racing the person typing.
 */
function PromptPanel({
  module,
  onDirtyChange,
  onModuleChange,
}: {
  module: AdminModule;
  onDirtyChange: (dirty: boolean) => void;
  onModuleChange: (module: AdminModule) => void;
}) {
  const path = `/modules/${module.code}/prompts`;
  const { data, error, isLoading, mutate } = useApi<VersionList>(path);
  const { user } = useAuth();
  const timezone = user?.timezone || browserTimezone();
  const toast = useToast();
  const confirm = useConfirm();

  const versions = React.useMemo(() => data?.versions ?? [], [data]);
  const active = versions.find((version) => version.active) ?? null;
  const base = active?.body ?? "";

  const [draft, setDraft] = React.useState<string | null>(null);
  const [note, setNote] = React.useState("");
  const [activate, setActivate] = React.useState(true);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [viewing, setViewing] = React.useState<PromptVersion | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [rowError, setRowError] = React.useState<Record<string, string>>({});

  const text = draft ?? base;
  const changed = draft !== null && draft !== base;
  const dirty = changed || note.trim() !== "";
  const over = text.length > MAX_PROMPT_CHARS;

  React.useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const bodyId = React.useId();
  const noteId = React.useId();

  function discard() {
    setDraft(null);
    setNote("");
    setErrors({});
    setFormError(null);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = createPromptVersionSchema.safeParse({
      body: text,
      note: note.trim() || undefined,
      activate,
    });
    if (!parsed.success) {
      setErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setErrors({});

    setSaving(true);
    let result: VersionResult;
    try {
      result = await api<VersionResult>(path, { method: "POST", body: parsed.data });
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setFormError(apiErrorMessage(err, "Could not save that version"));
      return;
    } finally {
      setSaving(false);
    }

    // Saved. Nothing after this line may be reported as a failure to save: the
    // row exists, and a retry would write a second, identical version.
    setDraft(null);
    setNote("");
    onModuleChange(result.module);
    void mutate(withVersion(result.version));
    toast.success(
      result.version.active
        ? `Version ${result.version.version} saved and active`
        : `Version ${result.version.version} saved`,
      {
        description: result.version.active
          ? "Every analysis from now on is asked with it."
          : active
            ? `Version ${active.version} is still the one in use. Activate the new one from the history.`
            : "It is in the history. Activate it to start using it.",
      }
    );
  }

  /**
   * Confirmed, where saving a new version is not: this one swaps the live
   * prompt with nothing typed, from a row somebody may have pressed while
   * reading the history. It is reversible - activate the other one back - but
   * every analysis in between is asked with it, for every customer.
   */
  async function activateVersion(version: PromptVersion) {
    setViewing(null);
    const ok = await confirm({
      title: `Activate version ${version.version}?`,
      message: (
        <>
          Every analysis from now on uses version {version.version}, for every customer. Analyses
          already on the record keep the version they were made with.
        </>
      ),
      confirmLabel: `Activate version ${version.version}`,
      cancelLabel: "Cancel",
    });
    if (!ok) return;

    setRowError({});
    setBusyId(version.id);
    let result: VersionResult;
    try {
      result = await api<VersionResult>(`${path}/${version.id}/activate`, { method: "POST" });
    } catch (err) {
      setRowError({ [version.id]: apiErrorMessage(err, "Could not activate that version") });
      return;
    } finally {
      setBusyId(null);
    }

    onModuleChange(result.module);
    void mutate(withVersion(result.version));
    toast.success(`Version ${version.version} is active`);
  }

  /** An older text as the starting point for the next version. Nothing is saved. */
  async function loadIntoEditor(version: PromptVersion) {
    if (changed) {
      const discard = await confirm({
        title: "Replace what is in the editor?",
        message: "Your unsaved changes to the prompt will be lost.",
        confirmLabel: "Replace",
        cancelLabel: "Keep editing",
        danger: true,
      });
      if (!discard) return;
    }
    setDraft(version.body === base ? null : version.body);
    setErrors({});
    setViewing(null);
  }

  if (error && !data) return <ErrorState error={error} onRetry={() => void mutate()} />;

  if (isLoading || !data) {
    return (
      <div className="modules-loading">
        <Spinner className="spinner--lg" />
        <span className="sr-only">Loading the prompt</span>
      </div>
    );
  }

  const shown = active ?? module.prompt;

  return (
    <>
      <Card>
        <CardHeader
          title={shown ? `Active version ${shown.version}` : "No active version"}
          subtitle={
            shown && (
              <span className="prompt-active">
                <code className="prompt-hash">{shown.hash}</code>
                <InfoHint
                  label="What the hash is for"
                  text="Every analysis stores this hash in assessment_attempts.model, which traces it back to this prompt."
                />
              </span>
            )
          }
        />
        <CardBody>
          <Form onSubmit={save}>
            <Field
              label="System prompt"
              htmlFor={bodyId}
              error={errors.body}
              info="Sent as the system message, exactly as written. Leave the checklist out: it is added to each request from Configuration."
              labelAction={
                <span className={cn("prompt-count", over && "prompt-count--over")}>
                  {text.length.toLocaleString()} / {MAX_PROMPT_CHARS.toLocaleString()}
                </span>
              }
            >
              <textarea
                id={bodyId}
                className={cn("input prompt-editor", errors.body && "input--invalid")}
                aria-invalid={Boolean(errors.body) || undefined}
                spellCheck={false}
                value={text}
                onChange={(event) => {
                  setDraft(event.target.value);
                  if (errors.body) setErrors((prev) => without(prev, "body"));
                }}
              />
            </Field>

            <Field label="Note" htmlFor={noteId} error={errors.note}>
              <Input
                id={noteId}
                maxLength={200}
                placeholder="What changed and why"
                invalid={Boolean(errors.note)}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                  if (errors.note) setErrors((prev) => without(prev, "note"));
                }}
              />
            </Field>

            <FormMessage>{formError}</FormMessage>

            <div className="prompt-actions">
              <Switch inline label="Activate now" checked={activate} onChange={setActivate} />

              <div className="prompt-actions__buttons">
                <Button variant="secondary" size="sm" disabled={!dirty || saving} onClick={discard}>
                  Discard
                </Button>
                {/* Only once the text differs. The same body again would be a
                    new version number over an identical prompt, and the hash
                    would say so. */}
                <Button type="submit" size="sm" loading={saving} disabled={!changed}>
                  Save as new version
                </Button>
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Versions" />
        <CardBody>
          <Table
            minWidth="md"
            head={
              <>
                <Th>Version</Th>
                <Th>Note</Th>
                <Th hideBelow="lg">Saved by</Th>
                <Th hideBelow="sm">Saved</Th>
                <Th hideBelow="md">Hash</Th>
                <Th align="right">Actions</Th>
              </>
            }
          >
            {versions.length === 0 && (
              <tr>
                <TdSpan colSpan={6}>
                  <EmptyState
                    icon={<History />}
                    title="No versions yet"
                    description="Save the prompt above to create the first one."
                  />
                </TdSpan>
              </tr>
            )}

            {versions.map((version) => (
              <Tr key={version.id}>
                <Td>
                  <span className="version-cell">
                    <span className="tnum">v{version.version}</span>
                    {version.active && <Badge tone="ok">Active</Badge>}
                  </span>
                  {rowError[version.id] && (
                    <p role="alert" className="row-error">
                      {rowError[version.id]}
                    </p>
                  )}
                </Td>
                <Td className={cn("version-note", !version.note && "subtle")}>
                  {version.note ?? "No note"}
                </Td>
                <Td hideBelow="lg" className="muted">
                  {authorName(version)}
                </Td>
                <Td hideBelow="sm" className="muted tnum">
                  {formatDayTimeIn(version.created_at, timezone)}
                </Td>
                <Td hideBelow="md">
                  <code className="prompt-hash">{version.hash}</code>
                </Td>
                <Td align="right">
                  <div className="row-actions">
                    <IconButton
                      size="sm"
                      label={`View version ${version.version}`}
                      onClick={() => setViewing(version)}
                    >
                      <Eye />
                    </IconButton>
                    {!version.active && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={busyId === version.id}
                        disabled={busyId !== null}
                        onClick={() => void activateVersion(version)}
                      >
                        Activate
                      </Button>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>
        </CardBody>
      </Card>

      {viewing && (
        <Modal
          open
          onClose={() => setViewing(null)}
          title={
            <span className="version-cell">
              Version {viewing.version}
              {viewing.active && <Badge tone="ok">Active</Badge>}
            </span>
          }
          className="dialog--wide"
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => void loadIntoEditor(viewing)}>
                Load into editor
              </Button>
              {!viewing.active && (
                <Button
                  size="sm"
                  disabled={busyId !== null}
                  onClick={() => void activateVersion(viewing)}
                >
                  Activate
                </Button>
              )}
            </>
          }
        >
          <dl className="version-facts">
            <div>
              <dt>Saved by</dt>
              <dd>{authorName(viewing)}</dd>
            </div>
            <div>
              <dt>Saved</dt>
              <dd className="tnum">{formatDayTimeIn(viewing.created_at, timezone)}</dd>
            </div>
            <div>
              <dt>Hash</dt>
              <dd>
                <code className="prompt-hash">{viewing.hash}</code>
              </dd>
            </div>
            <div className="version-facts__wide">
              <dt>Note</dt>
              <dd className={cn(!viewing.note && "subtle")}>{viewing.note ?? "No note"}</dd>
            </div>
          </dl>
          <pre className="prompt-view">{viewing.body}</pre>
        </Modal>
      )}
    </>
  );
}

/**
 * The list as the API now has it, without waiting to ask: the version the call
 * returned goes in (new at the top, or replacing itself), and if it is active,
 * nothing else is. SWR still revalidates behind it.
 */
function withVersion(next: PromptVersion) {
  return (prev: VersionList | undefined): VersionList | undefined => {
    if (!prev) return prev;
    const versions = prev.versions.map((version) =>
      version.id === next.id ? next : next.active ? { ...version, active: false } : version
    );
    const known = versions.some((version) => version.id === next.id);
    return { versions: known ? versions : [next, ...versions] };
  };
}

function without(errors: Record<string, string>, key: string): Record<string, string> {
  const next = { ...errors };
  delete next[key];
  return next;
}

/**
 * Null `created_by` is either bootstrap's seed, which ran as nobody, or an
 * author whose account has since gone (`on delete set null`). The row cannot
 * say which, so neither word is claimed.
 */
function authorName(version: PromptVersion): string {
  if (!version.created_by) return "Unknown";
  return version.created_by.name ?? "Unnamed user";
}
